import { path } from 'ramda'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import styled from 'styled-components'
import {
  RadioGroup,
  RadioItem,
  Button,
  TextLinkButton,
  useDialogControllers,
  Text
} from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { breakpoints, colors } from '@/styles'
import NoCreditHistoryModal from '@/components/no-credit-history-modal'
import CreditScoreImpactDialog from './credit-score-impact-dialog'

const PanelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px 0 0;
  margin: 0 52px;

  &:last-of-type {
    border: none;
  }
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 0 20px;
  }
`

const PanelText = styled.div`
  font-size: 14px;
  max-width: 460px;
  color: ${colors.regular};
  margin: 0px;
  margin-right: 40px;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 12px;
    margin-right: 20px;
  }
`

const CreditCheckPanelRow = styled(PanelRow)`
  margin: 32px 43px 0;

  &:last-of-type {
    border-top: 1px solid ${vars.borderColor.neutralLight};
  }
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 32px 20px 0;
  }
`

const CreditCheckRow = styled.div`
  display: flex;
  width: 100%;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    display: block;
  }
`

const CreditCheckLeftWrapper = styled.div`
  width: 100%;
  max-width: 280px;
`

const CreditCheckLeftWrapperTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
`

const CreditCheckLeftWrapperText = styled.p`
  font-size: 12px;
  line-height: 18px;
  color: ${colors.foregroundTertiary};
`

const CreditCheckRightWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const CreditCheckRightWrapperTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
`

const RadioGroupWrapper = styled.div`
  display: flex;
  gap: 10px;
`

const ButtonWrapper = styled.div`
  margin-top: 24px;
  max-width: 118px;
  margin-left: auto;
`

const LivScoreForm = ({ editable, editProps, isSubmitted, isFailed }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const { visible, showModal, closeModal } = useDialogControllers({
    noCreditHistory: false,
    creditScoreImpact: false
  })

  const isInternationalUser = useSelector(path(['profile', 'international_user'])) === '1'
  const [hasHistory, setHasHistory] = useState(isInternationalUser ? 'false' : 'true')
  const [userHasInteracted, setUserHasInteracted] = useState(false)
  useEffect(() => {
    if (userHasInteracted && hasHistory === 'false') {
      showModal('noCreditHistory')()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHistory, userHasInteracted])

  const navigateToCreditCheck = () => {
    history.push({
      pathname: '/profile/trust-score/edit/credit-check',
      state: editProps
    })
  }

  if (!editable) return null

  return (
    <>
      {(isInternationalUser || !isSubmitted || isFailed) && (
        <>
          <CreditCheckPanelRow>
            <CreditCheckRow>
              <CreditCheckLeftWrapper>
                <PanelText>
                  <CreditCheckLeftWrapperTitle>
                    {t('r.profile.edit.credit_check.title', 'Credit Check')}
                  </CreditCheckLeftWrapperTitle>

                  <CreditCheckLeftWrapperText>
                    {t(
                      'r.profile.edit.liv_score_credit_check.text',
                      'Used by landlords to evaluate how reliable applicants are and assess their ability to pay rent on time.'
                    )}
                  </CreditCheckLeftWrapperText>
                  <Text size="xsmall">
                    <TextLinkButton
                      onClick={() => {
                        showModal('creditScoreImpact')()
                      }}
                    >
                      {t(
                        'r.profile.edit.liv_score_credit_check.modal_open.text',
                        'How does this impact my credit score?'
                      )}
                    </TextLinkButton>
                  </Text>
                </PanelText>
              </CreditCheckLeftWrapper>
              <CreditCheckRightWrapper>
                <CreditCheckRightWrapperTitle>
                  {t(
                    'r.CreditCheckRightWrapperTitlerofile.edit.liv_score_credit_check_radio_button_title.text',
                    'Do you have credit history in Canada?'
                  )}
                </CreditCheckRightWrapperTitle>
                <RadioGroupWrapper>
                  <RadioGroup
                    id="trust_score_credit_check_yes"
                    tone="positive"
                    value={hasHistory}
                    onChange={e => {
                      setHasHistory(e.target.value)
                      setUserHasInteracted(true)
                    }}
                  >
                    <RadioItem value="true" label={t('b.components.dialog.yes.button', 'Yes')} />
                  </RadioGroup>
                  <RadioGroup
                    id="trust_score_credit_check_no"
                    tone="positive"
                    value={hasHistory}
                    onChange={e => {
                      setHasHistory(e.target.value)
                      setUserHasInteracted(true)
                    }}
                  >
                    <RadioItem value="false" label={t('b.components.dialog.no.button', 'No')} />
                  </RadioGroup>
                </RadioGroupWrapper>
                {hasHistory === 'true' && (
                  <ButtonWrapper>
                    <Button
                      type="submit"
                      size="small"
                      variant="solid"
                      onClick={navigateToCreditCheck}
                      disabled={!hasHistory}
                    >
                      {t('b.profile.edit.get_started.button', 'Get Started')}
                    </Button>
                  </ButtonWrapper>
                )}
              </CreditCheckRightWrapper>
            </CreditCheckRow>
          </CreditCheckPanelRow>
        </>
      )}

      <NoCreditHistoryModal
        visible={visible.noCreditHistory}
        onClose={closeModal('noCreditHistory')}
        updateUser={payload => dispatch.livScore.updateInternationalUser(payload)}
      />
      <CreditScoreImpactDialog
        visible={visible.creditScoreImpact}
        onClose={closeModal('creditScoreImpact')}
      />
    </>
  )
}

export default memo(LivScoreForm)
